import React, { useMemo, useState } from 'react';
import { Button, Card, Flex, Input, Popconfirm, Space, Table, Tag, Typography } from 'antd';
import { DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import type { UserKnownWord } from '@lingoping/core';
import { groupKnownWords, plural, visuallyHidden, type KnownGroup } from './format.js';
import { tourTarget } from './tourTargets.js';

const { Text, Paragraph } = Typography;

interface Props {
  knownWords: UserKnownWord[];
  onAdd: (word: string) => Promise<void>;
  onRemoveGroup: (base: string) => Promise<void>;
}

const MAX_FORMS_SHOWN = 12;

export const KnownWordsTab: React.FC<Props> = ({ knownWords, onAdd, onRemoveGroup }) => {
  const [search, setSearch] = useState('');
  const [newWord, setNewWord] = useState('');
  const [adding, setAdding] = useState(false);

  const allGroups = useMemo(() => groupKnownWords(knownWords), [knownWords]);
  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allGroups;
    return allGroups.filter(
      (g) =>
        g.base.toLowerCase().includes(q) || g.forms.some((f) => f.lemma.toLowerCase().includes(q))
    );
  }, [allGroups, search]);

  const handleAdd = async () => {
    const word = newWord.trim();
    if (!word || adding) return;
    setAdding(true);
    try {
      await onAdd(word);
      setNewWord('');
    } finally {
      setAdding(false);
    }
  };

  return (
    <Card
      title={
        <Flex align="baseline" gap={8} wrap>
          <span>Known words</span>
          <Text type="secondary" style={{ fontWeight: 400, fontSize: 13 }}>
            {plural(allGroups.length, 'word')} ({plural(knownWords.length, 'form')})
          </Text>
        </Flex>
      }
    >
      <Paragraph type="secondary">Words on this list are never shown to you again.</Paragraph>

      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Space.Compact style={{ width: 340, maxWidth: '100%' }} {...tourTarget('known-add')}>
          <Input
            placeholder="Add a word you already know"
            aria-label="Add a word you already know"
            value={newWord}
            onChange={(e) => setNewWord(e.target.value)}
            onPressEnter={handleAdd}
          />
          <Button type="primary" loading={adding} disabled={!newWord.trim()} onClick={handleAdd}>
            Add
          </Button>
        </Space.Compact>

        <Input
          placeholder="Search known words"
          aria-label="Search known words"
          prefix={<SearchOutlined />}
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 240, maxWidth: '100%' }}
        />
      </Flex>

      <Table
        dataSource={groups}
        rowKey="key"
        pagination={{ pageSize: 10, hideOnSinglePage: true, showSizeChanger: false }}
        scroll={{ x: 520 }}
        locale={{
          emptyText: search
            ? 'No known words match your search.'
            : 'No known words yet. Mark a word “I know it” and it appears here.',
        }}
        columns={[
          {
            title: 'Word',
            dataIndex: 'base',
            key: 'base',
            width: 180,
            sorter: (a: KnownGroup, b: KnownGroup) => a.base.localeCompare(b.base),
            render: (text: string) => <Text strong>{text}</Text>,
          },
          {
            title: 'Forms you know',
            key: 'forms',
            render: (_: unknown, group: KnownGroup) => (
              <Flex wrap="wrap" gap={4}>
                {group.forms.slice(0, MAX_FORMS_SHOWN).map((f) => (
                  <Tag key={f.id} style={{ margin: 0 }}>
                    {f.lemma}
                  </Tag>
                ))}
                {group.forms.length > MAX_FORMS_SHOWN && (
                  <Text type="secondary">+{group.forms.length - MAX_FORMS_SHOWN} more</Text>
                )}
              </Flex>
            ),
          },
          {
            title: 'Added',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 120,
            sorter: (a: KnownGroup, b: KnownGroup) => a.created_at.localeCompare(b.created_at),
            render: (d: string) => new Date(d).toLocaleDateString(),
          },
          {
            title: <span style={visuallyHidden}>Remove</span>,
            key: 'action',
            width: 70,
            render: (_: unknown, group: KnownGroup) => (
              <Popconfirm
                title={`Remove “${group.base}”?`}
                description="It may be offered to you again in decks and imports."
                okText="Remove"
                cancelText="Cancel"
                onConfirm={() => onRemoveGroup(group.base)}
              >
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  aria-label={`Remove ${group.base} from known words`}
                />
              </Popconfirm>
            ),
          },
        ]}
      />
    </Card>
  );
};
